import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type { ArticleRow, DocumentRow, Paginated } from "@/lib/types";

// ---------------------------------------------------------------------------
// Queues
// ---------------------------------------------------------------------------

/** GET /api/v1/documents/pending/ — items the actor needs to action. */
export function usePendingDocuments() {
  return useQuery<Paginated<DocumentRow>>({
    queryKey: ["documents", "pending"],
    queryFn: async () => {
      const { data } = await api.get<Paginated<DocumentRow>>("/documents/pending/");
      return data;
    },
  });
}

/** GET /api/v1/articles/pending/ — articles the actor needs to action. */
export function usePendingArticles() {
  return useQuery<Paginated<ArticleRow>>({
    queryKey: ["articles", "pending"],
    queryFn: async () => {
      const { data } = await api.get<Paginated<ArticleRow>>("/articles/pending/");
      return data;
    },
  });
}

/** Combined pending-verification count (docs + articles), for the sidebar
 *  badge. Same query keys as the per-type hooks above, so React Query
 *  shares the cache — opening the Verification page reuses these fetches
 *  rather than re-issuing them. Caller passes `enabled: false` for
 *  non-directors so we don't churn 403s. */
export function usePendingVerificationCount(opts: { enabled?: boolean } = {}) {
  const enabled = opts.enabled ?? true;
  const docs = useQuery<Paginated<DocumentRow>>({
    enabled,
    queryKey: ["documents", "pending"],
    queryFn: async () => {
      const { data } = await api.get<Paginated<DocumentRow>>("/documents/pending/");
      return data;
    },
  });
  const arts = useQuery<Paginated<ArticleRow>>({
    enabled,
    queryKey: ["articles", "pending"],
    queryFn: async () => {
      const { data } = await api.get<Paginated<ArticleRow>>("/articles/pending/");
      return data;
    },
  });
  // `count` is the TOTAL across all pages (DRF default). results.length
  // would only reflect the first page and undercount when the queue is
  // long enough to paginate.
  const count = (docs.data?.count ?? 0) + (arts.data?.count ?? 0);
  const isLoading = docs.isLoading || arts.isLoading;
  return { count, isLoading };
}

// ---------------------------------------------------------------------------
// Mutations — approve / reject. Both invalidate the queue queries on
// success so the row disappears from the UI without a manual refetch,
// and the dashboard counts re-fetch too.
// ---------------------------------------------------------------------------

function useDocumentDecision(decision: "approve" | "reject") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<DocumentRow>(`/documents/${id}/${decision}/`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

function useArticleDecision(decision: "approve" | "reject") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string) => {
      const { data } = await api.post<ArticleRow>(`/articles/${slug}/${decision}/`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["articles"] });
    },
  });
}

export const useApproveDocument = () => useDocumentDecision("approve");
export const useRejectDocument  = () => useDocumentDecision("reject");
export const useApproveArticle  = () => useArticleDecision("approve");
export const useRejectArticle   = () => useArticleDecision("reject");
